<template>
  <div class="min-h-screen bg-surface">
    <div class="max-w-3xl mx-auto px-4 py-12">
      <NuxtLink to="/auth" class="inline-flex items-center gap-1 text-primary mb-8 hover:underline">
        <span class="material-symbols-rounded" style="font-size: 18px;">arrow_back</span>
        Volver
      </NuxtLink>

      <h1 class="text-display-small font-bold text-on-surface mb-2">Términos y Condiciones de Uso</h1>
      <p class="text-body-medium text-on-surface-variant mb-8">Última actualización: 26 de marzo de 2026</p>

      <div class="prose prose-neutral max-w-none space-y-6 text-body-large text-on-surface/90 leading-relaxed">

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">1. Identificación del prestador</h2>
          <ul class="list-none space-y-1 pl-0">
            <li><strong>Titular:</strong> Dieter José Lorenzo Molina</li>
            <li><strong>NIF:</strong> 54891162Y</li>
            <li><strong>Domicilio:</strong> Ciempozuelos, Madrid, España</li>
            <li><strong>Correo electrónico:</strong> dieter@comandaplus.com</li>
          </ul>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">2. Objeto del servicio</h2>
          <p>ComandaPlus es una plataforma de software como servicio (SaaS) diseñada para la gestión integral de establecimientos de hostelería en España. El servicio incluye: gestión de pedidos, salón y mesas, carta digital, caja registradora, gestión de equipo y analíticas de negocio.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">3. Condiciones de uso</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>El usuario debe ser mayor de 18 años y tener capacidad legal para contratar.</li>
            <li>El usuario se compromete a proporcionar información veraz y actualizada durante el registro.</li>
            <li>El usuario es responsable de mantener la confidencialidad de sus credenciales de acceso.</li>
            <li>Queda prohibido el uso del servicio para fines ilícitos o que contravengan la legislación vigente.</li>
            <li>El usuario es responsable de los datos que introduzca en la plataforma, incluyendo los datos de sus empleados y clientes.</li>
          </ul>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">4. Disponibilidad del servicio</h2>
          <p>ComandaPlus se esfuerza por mantener la plataforma disponible de forma continua. No obstante, no garantizamos una disponibilidad del 100% y no seremos responsables de interrupciones derivadas de mantenimiento programado, fallos de terceros proveedores, fuerza mayor o causas ajenas a nuestro control.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">5. Propiedad intelectual</h2>
          <p>Todos los contenidos de la plataforma (código fuente, diseño, marca, logotipos, textos) son propiedad de Dieter José Lorenzo Molina o se utilizan con licencia. Queda prohibida su reproducción, distribución o transformación sin autorización expresa.</p>
          <p>Los datos que el usuario introduce en la plataforma son propiedad del usuario. ComandaPlus únicamente los trata conforme a la <NuxtLink to="/legal/privacidad" class="text-primary hover:underline">Política de Privacidad</NuxtLink>.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">6. Limitación de responsabilidad</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>ComandaPlus no se responsabiliza de las decisiones comerciales que el usuario tome basándose en los datos de la plataforma.</li>
            <li>No nos hacemos responsables de pérdidas derivadas de un uso indebido de la plataforma o del incumplimiento de estas condiciones por parte del usuario.</li>
            <li>Nuestra responsabilidad máxima se limita al importe de las cuotas pagadas por el usuario en los 12 meses anteriores al hecho generador.</li>
            <li>ComandaPlus no es un software de facturación certificado. El usuario es responsable de cumplir con sus obligaciones fiscales mediante los medios legalmente establecidos.</li>
          </ul>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">7. Resolución y baja del servicio</h2>
          <p>El usuario puede darse de baja en cualquier momento desde su perfil en la aplicación. Al darse de baja:</p>
          <ul class="list-disc pl-6 space-y-1">
            <li>Se cancelará el acceso a la plataforma.</li>
            <li>Los datos se conservarán durante los plazos legales obligatorios (mínimo 4 años para datos fiscales).</li>
            <li>El usuario puede solicitar la exportación de sus datos antes de la baja.</li>
          </ul>
          <p>ComandaPlus se reserva el derecho de suspender o cancelar cuentas que incumplan estas condiciones, previo aviso al usuario salvo en casos de urgencia.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">8. Modificaciones</h2>
          <p>Nos reservamos el derecho de modificar estas condiciones. Los cambios se notificarán al usuario por correo electrónico o mediante aviso en la plataforma con al menos 30 días de antelación. El uso continuado del servicio tras la modificación implica la aceptación de las nuevas condiciones.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">9. Legislación aplicable y jurisdicción</h2>
          <p>Estos términos se rigen por la legislación española. Para la resolución de cualquier controversia, las partes se someten a los Juzgados y Tribunales de Madrid, salvo que la normativa de protección al consumidor disponga otra cosa.</p>
        </section>

        <section>
          <h2 class="text-title-large font-semibold text-on-surface mt-8 mb-3">10. Contacto</h2>
          <p>Para cualquier consulta relacionada con estos términos, puedes contactarnos en <strong>dieter@comandaplus.com</strong>.</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: false,
})

useHead({
  title: 'Términos y Condiciones — ComandaPlus',
})
</script>
